import {useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {useSingleRunModeContext} from '../../components/MultiRunWorkspace/SingleRunMode/SingleRunModeContext';
import {useRunIssues} from '../../components/RunSelector/hooks/useRunIssues';
import {type ApolloError} from '../../state/graphql/apolloWithAbort';
import {convertIssuesToTimestampKeyTuples} from './issuesByTimestampReducer';
import {useIssuesState} from './store';
import {type ActiveRun, type Issue, type IssueLine} from './types';
import {
  useDidRunOnCoreweave,
  useRampFlagCoreweaveIssues,
} from './useCoreweaveIssues';
import {useIssueTimestamps} from './useIssueTimestamps';
import {useRunIssuesDashboard} from './useRunIssuesDashboard';
import {groupIssues, parseCacheKey} from './utils';

const EMPTY_ISSUE_LINES: IssueLine[] = [];
// No-op functions with proper signatures
const noOp = () => {};
const noOpSetActiveRun = (_run: ActiveRun | null) => {};
const noOpSetSelectedIssue = (_issueId: string | null) => {};
const disabledHandleIssueInteraction =
  (_event: 'hover' | 'unhover', _issueId: string | null) => () => {};

function useIssuesCapabilities() {
  const canUseIssuesContext = useRampFlagCoreweaveIssues();
  const {isSingleRun} = useSingleRunModeContext();
  return {
    canUseIssuesContext,
    canFetch: isSingleRun && canUseIssuesContext,
  };
}

export function deriveActiveIssueError({
  activeRunName,
  ranOnCoreweave,
  ranOnCoreweaveLoading,
  runIssueError,
}: {
  activeRunName: string;
  ranOnCoreweave: boolean;
  ranOnCoreweaveLoading: boolean;
  runIssueError?: ApolloError;
}): boolean {
  if (!activeRunName) {
    return false;
  }

  const didRunOnCoreweave = ranOnCoreweave && !ranOnCoreweaveLoading;
  return !!runIssueError || !didRunOnCoreweave;
}

function useActiveIssuesQuery(): {
  activeIssues: Issue[];
  activeErrorDrawer: boolean;
  error?: ApolloError;
  loading: boolean;
  ranOnCoreweave: boolean;
} {
  const {entityName = '', projectName = ''} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const canUseIssuesContext = useRampFlagCoreweaveIssues();
  const activeRun = useActiveIssuesRun();
  const activeRunName = activeRun?.name ?? '';
  const {ranOnCoreweave, loading: ranOnCoreweaveLoading} = useDidRunOnCoreweave(
    projectName,
    entityName,
    activeRunName,
    canUseIssuesContext
  );
  const {
    data: activeIssues,
    error,
    loading,
  } = useRunIssues({
    ranOnCoreweave,
    entityName,
    projectName,
    runName: activeRunName,
  });

  const activeErrorDrawer = deriveActiveIssueError({
    activeRunName,
    ranOnCoreweave,
    ranOnCoreweaveLoading,
    runIssueError: error,
  });

  return {activeIssues, activeErrorDrawer, error, loading, ranOnCoreweave};
}

export function useActiveErrorDrawer(): boolean {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const {activeErrorDrawer} = useActiveIssuesQuery();
  return canUseIssuesContext && activeErrorDrawer;
}

export function useActiveIssues(): Issue[] {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const {activeIssues, activeErrorDrawer} = useActiveIssuesQuery();
  if (!canUseIssuesContext || activeErrorDrawer) {
    return [];
  }
  return activeIssues;
}

export function useActiveRun(): ActiveRun | null {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const activeRun = useActiveIssuesRun();
  return canUseIssuesContext ? (activeRun ?? null) : null;
}

export function useGroupedIssues(): Issue[][] {
  return groupIssues(useActiveIssues());
}

export function useAnnotationActive(): boolean {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const annotationActive = useIssuesState(state => state.annotationActive);
  return canUseIssuesContext ? annotationActive : false;
}

export function useHandleIssueInteraction() {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const handleIssueInteraction = useIssuesState(
    state => state.handleIssueInteraction
  );
  return canUseIssuesContext
    ? handleIssueInteraction
    : disabledHandleIssueInteraction;
}

export function useHoveredIssue(): string | null {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const hoveredIssue = useIssuesState(state => state.hoveredIssue);
  return canUseIssuesContext ? hoveredIssue : null;
}

function getHoveredIssue(): string | null {
  return useIssuesState.getState().hoveredIssue;
}

/**
 * Reads the hovered issue without re-rendering when it changes, and calls
 * onChange after each change while enabled. Use it for canvas drawing that
 * only needs a redraw; useHoveredIssue re-renders the component on every
 * hover. Callers pass enabled only when they draw issue lines, so charts
 * without them neither subscribe nor redraw. The returned getter is the same
 * function on every render, so it can go in chart options.
 */
export function useHoveredIssueReader({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: () => void;
}): () => string | null {
  useEffect(
    function subscribeToHoveredIssue() {
      if (!enabled) {
        return undefined;
      }
      return useIssuesState.subscribe(function onIssuesChange(state, prev) {
        if (state.hoveredIssue !== prev.hoveredIssue) {
          onChange();
        }
      });
    },
    [enabled, onChange]
  );

  return getHoveredIssue;
}

export function useIssueRampStatus(): 'enabled' | 'disabled' {
  const {canFetch} = useIssuesCapabilities();
  return canFetch ? 'enabled' : 'disabled';
}

export function useRunGrafanaDashboard(): string {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const {entityName = '', projectName = ''} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const activeRun = useActiveIssuesRun();
  const activeRunName = activeRun?.name ?? '';
  const {ranOnCoreweave} = useActiveIssuesQuery();
  const {data: runGrafanaDashboard} = useRunIssuesDashboard({
    ranOnCoreweave,
    entityName,
    projectName,
    runName: activeRunName,
  });

  if (!canUseIssuesContext) {
    return '';
  }
  return runGrafanaDashboard ?? '';
}

export function useSelectedIssue(): string | null {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const selectedIssue = useIssuesState(state => state.selectedIssue);
  return canUseIssuesContext ? selectedIssue : null;
}

export function useSetActiveRun() {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const setActiveRun = useIssuesState(state => state.setActiveRun);
  return canUseIssuesContext ? setActiveRun : noOpSetActiveRun;
}

export function useSetSelectedIssue() {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const setSelectedIssue = useIssuesState(state => state.setSelectedIssue);
  return canUseIssuesContext ? setSelectedIssue : noOpSetSelectedIssue;
}

export function useToggleIssues() {
  const {canUseIssuesContext} = useIssuesCapabilities();
  const toggleIssues = useIssuesState(state => state.toggleIssues);
  return canUseIssuesContext ? toggleIssues : noOp;
}

export function useIssueLinesForCacheKey(cacheKey: string): IssueLine[] {
  const {canFetch} = useIssuesCapabilities();
  const {isSingleRun, run} = useSingleRunModeContext();
  const {entityName = '', projectName = ''} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const {activeIssues} = useActiveIssuesQuery();
  const {xAxis, zoom} = useMemo(() => parseCacheKey(cacheKey), [cacheKey]);
  const isNullNullZoom = zoom[0] == null && zoom[1] == null;

  const {keyByTimestamp: specificTimestamps} = useIssueTimestamps({
    entityName,
    projectName,
    runName: run?.name,
    runCreatedAt: run?.createdAt,
    xAxis,
    zoom,
    skip: !canFetch,
  });

  const {keyByTimestamp: fallbackTimestamps} = useIssueTimestamps({
    entityName,
    projectName,
    runName: run?.name,
    runCreatedAt: run?.createdAt,
    xAxis,
    zoom: [null, null],
    skip: !canFetch || isNullNullZoom || specificTimestamps.length > 0,
  });

  const keyByTimestamp = useMemo(() => {
    if (specificTimestamps.length > 0) {
      return specificTimestamps;
    }
    return fallbackTimestamps;
  }, [fallbackTimestamps, specificTimestamps]);

  return useMemo(() => {
    if (!canFetch || !keyByTimestamp.length) {
      return EMPTY_ISSUE_LINES;
    }
    return convertIssuesToTimestampKeyTuples(activeIssues, keyByTimestamp);
  }, [activeIssues, canFetch, keyByTimestamp]);
}

export function useActiveIssuesRun() {
  // In single run mode, the active run in the drawer is always the run we're viewing
  const {isSingleRun, run} = useSingleRunModeContext();
  // In multi run mode, the active run in the drawer is set when the issues column is clicked in the run selector.
  const issuesActiveRun = useIssuesState(state => state.activeRun);
  return isSingleRun ? run : issuesActiveRun;
}
